import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generateClassName } from '@klv/shared/utils/functions';
import Icon from '@klv/atoms/icon';
import { SelectFieldGroup } from '@klv/molecules/form-field/select';
import { Input } from '@klv/molecules/form-field';
import Toggle from '@klv/molecules/toggle';
import pricingData from '@klv/shared/pricing/static/us.json';
import creditRatios from '@klv/shared/pricing/static/credit-ratio.json';
import carrierFees from '@klv/shared/pricing/static/carrier-fees.json';
import * as styles from './styles/pricing-calulator.module.css';
import type { IPricingCalc } from './types';

const smsCreditRatios = creditRatios.sms;
const mmsCreditRatios = creditRatios.mms;
const smsCarrierFees = carrierFees.sms;
const mmsCarrierFees = carrierFees.mms;

type TSmsRatios = keyof typeof creditRatios['sms'];

type TMmsRatios = keyof typeof creditRatios['mms'];

type TSmsCarrierFees = keyof typeof carrierFees['sms'];

type TMmsCarrierFees = keyof typeof carrierFees['mms'];

type TValidLocales = TSmsRatios | TMmsRatios;

type TSmsPlans = keyof typeof pricingData['sms'];

const PricingCalculator = ({
  smsCredits,
  backgroundColor,
  className,
  onToggleChange,
  ...props
}: IPricingCalc) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [isSms, setIsSms] = useState(true);
  const [selectedLocale, setSelectedLocale] = useState('US');
  const [isOpen, setIsOpen] = useState(false);

  const creditType = isSms ? 'sms' : 'mms';
  const credits = useMemo(() => Object.keys(creditRatios[creditType]), [creditType]);

  const ratio: number = creditType === 'mms'
    ? mmsCreditRatios[selectedLocale as TMmsRatios]
    : smsCreditRatios[selectedLocale as TSmsRatios];

  const maxSends = Math.trunc(smsCredits / ratio);
  const maxSendsString = new Intl.NumberFormat(locale).format(maxSends);

  const price = pricingData.sms[smsCredits.toString() as TSmsPlans].price;

  const selectedFee: number = creditType === 'mms'
    ? mmsCarrierFees[selectedLocale as TMmsCarrierFees]
    : smsCarrierFees[selectedLocale as TSmsCarrierFees];

  const creditString = creditType === 'mms'
    ? t('organisms_pricing_calculator_mms')
    : t('organisms_pricing_calculator_sms');

  const pricePerSend = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(price / maxSends);

  const carrierFee = selectedFee ? new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(selectedFee) : '';

  const toggleCollapse = () => setIsOpen(!isOpen);

  const handleToggle = useCallback(() => {
    const previousSmsValue = isSms;
    setIsSms(!previousSmsValue);
    onToggleChange(!previousSmsValue ? 'sms' : 'mms');
  }, [isSms, onToggleChange]);

  const renderMaxSends = () => {
    if (creditType === 'mms' && mmsCreditRatios[selectedLocale as TMmsRatios] === 0) {
      return (
        <div className={styles.maxSendsContainer}>
          <div className={styles.notAvailable}>
            {t('organisms_pricing_calculator_mms_not_available')}
          </div>
        </div>
      );
    }
  
    return (
      <div className={styles.maxSendsContainer}>
        <div className={styles.maxSends}>
          {maxSendsString}
          {' '}
          {t('organisms_pricing_calculator_maxSends')}
        </div>
        <div className={styles.pricePerSend}>
          ({pricePerSend} {t('organisms_pricing_calculator_pricePerSend')})
        </div>
      </div>
    );
  };
  

  const renderCarrierFeeDisclaimer = () => {
    if (creditType === 'mms' && mmsCreditRatios[selectedLocale as TMmsRatios] === 0) {
      return null;
    }

    const carrierFeesForLocale = creditType === 'mms' 
      ? mmsCarrierFees[selectedLocale as TMmsCarrierFees]
      : smsCarrierFees[selectedLocale as TSmsCarrierFees];

    if (!carrierFeesForLocale) {
      return null;
    }

    return (
      <div className="disclaimer">
        * {t('organisms_pricing_calculator_carrierFeeDisclaimer')}{' '}
        {carrierFee}.{' '}
        {t('organisms_pricing_calculator_carrierFeeDisclaimerEnd')}
      </div>
    );
  };
  

  return (
    <div className={generateClassName([className, styles.pricingCalc])} {...props}>
      <div className={styles.collapsibleHeader}>
        <div className={styles.headline}>{t('organisms_pricing_calculator_headline')}</div>
        <Icon
          onClick={toggleCollapse}
          iconName="ChevronDown"
          className={`${styles.chevron} ${isOpen ? styles.open : ''}`}
        />
      </div>

      {isOpen && (
        <div>
          <div className={styles.flexContainer}>
            <div className={styles.destination}>
              <div className={`tagline ${styles.label}`}>
                {t('organisms_pricing_calculator_destination')}
              </div>
              <div className={styles.dropdownContainer}>
                <SelectFieldGroup className={styles.dropdown} $backgroundColor="light">
                  <Icon className={styles.icon} iconName="ChevronDown" />
                  <Input
                    className={styles.input}
                    $backgroundColor={backgroundColor}
                    aria-labelledby="pricing--sms-country"
                    as="select"
                    hasError={false}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                      setSelectedLocale(event.target.value as TValidLocales)
                    }
                    value={selectedLocale as TValidLocales}
                  >
                    {credits
                      .sort((a, b) => a.localeCompare(b))
                      .map((code) => {
                        const optionRatio =
                          creditType === 'mms'
                            ? mmsCreditRatios[code as TMmsRatios]
                            : smsCreditRatios[code as TSmsRatios];
                        return (
                          <option
                            className={styles.selectedLocale}
                            key={code}
                            value={code}
                            disabled={creditType === 'mms' && optionRatio === 0}
                          >
                            {t(`organisms_pricing_calculator_locales_${code}`, code)} (
                            {optionRatio === 0
                              ? t('organisms_pricing_calculator_not_available')
                              : `${optionRatio} ${
                                optionRatio === 1
                                  ? t('organisms_pricing_calculator_credit')
                                  : t('organisms_pricing_calculator_credits')
                              } /${creditString}`}
                            )
                          </option>
                        );
                      })}
                  </Input>
                </SelectFieldGroup>
              </div>
            </div>

            <div className={styles.toggleContainer}>
              <div className={`tagline ${styles.label}`}>
                {t('organisms_pricing_calculator_service_type')}
              </div>
              <Toggle
                className={styles.toggle}
                backgroundColor="white"
                falseText={t('organisms_pricing_calculator_mms')}
                setValue={handleToggle}
                value={isSms}
                trueText={t('organisms_pricing_calculator_sms')}
                variant="pricing"
              />
            </div>

            <div className={styles.desktop}>
              {renderMaxSends()}
              <div>
              {['sms', 'mms'].includes(creditType) && (
              <div className={styles.smsLimit}>
                {(creditType === 'mms' && mmsCreditRatios[selectedLocale as TMmsRatios] === 0) ? '' :
                  t(`organisms_pricing_calculator_${creditType}_limit`)
                }
              </div>
              )}
                {renderCarrierFeeDisclaimer()}
              </div>
            </div>
          </div>

          <div className={styles.mobile}>
            {renderMaxSends()}
            <div>
              {['sms', 'mms'].includes(creditType) && (
              <div className={styles.smsLimit}>
                {(creditType === 'mms' && mmsCreditRatios[selectedLocale as TMmsRatios] === 0) ? '' :
                  t(`organisms_pricing_calculator_${creditType}_limit`)
                }
              </div>
              )}
              {renderCarrierFeeDisclaimer()}
            </div>
          </div>
          <div className={styles.divider} />
        </div>
      )}
    </div>
  );
};

export default PricingCalculator;

